import { useCallback, useContext, useEffect, useRef } from "react";
import { useNavigate as useRRNavigate, matchPath } from "react-router-dom";

import { OrNumber, RouteDestination } from "config/types";
import {
  JourneySectionContext,
  SectionContextValue,
} from "modules/managers/JourneySectionProvider";
import { resolvePath } from "utils/resolvePath";
import { scrollToTop } from "utils/scrollToTop";

import { defaultTo, findLast, isEmpty, map } from "lodash-es";

import { useJourney } from "./useJourney";

type State = Record<string, unknown>;

export interface NavigateOptions {
  replace?: boolean;
  state?: State;
}

export interface NavigateFunction {
  (to: RouteDestination, options?: NavigateOptions): void;
  (delta: number): void;
}

export interface NavigateHookOptions {
  context?: { journeySection?: SectionContextValue };
}

export const useNavigate = (baseState?: State, cxt?: NavigateHookOptions) => {
  const rrNavigate = useRRNavigate();
  const { anyJourneysActive } = useJourney();

  const context = defaultTo(cxt?.context?.journeySection, useContext(JourneySectionContext));
  const { sections, setActive } = context;
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = true;
  });

  const navigate: NavigateFunction = useCallback(
    (to: OrNumber<RouteDestination>, options: NavigateOptions = {}) => {
      const path = resolvePath(to as RouteDestination, "/");
      if (anyJourneysActive) {
        const sectionsWithMatches = map(sections, section => {
          const match = matchPath({ path: section.route as string, end: true }, to as string);
          return { ...section, match };
        });

        const matchingTargetSection = findLast(sectionsWithMatches, section => {
          const hasRoute = !isEmpty(section.route);
          const hasMatch = section.match !== null;
          return hasRoute && hasMatch;
        });

        if (matchingTargetSection?.key && matchingTargetSection?.match) {
          const match = matchingTargetSection.match;
          const targetSectionKey = matchingTargetSection.key;
          const payload = { ...baseState, ...options?.state, params: match?.params };

          if (!isEmpty(targetSectionKey)) {
            setActive(targetSectionKey as string, payload);
            scrollToTop({ behavior: "auto" });
          }
          return;
        }
      }

      if (activeRef.current) {
        if (typeof to === "number") {
          rrNavigate(to);
        } else if (options.replace) {
          rrNavigate(path.pathname, {
            replace: true,
            state: { ...baseState, ...options.state },
          });
        } else {
          rrNavigate(path.pathname, {
            state: { ...baseState, ...options.state },
          });
        }
      }
    },
    [anyJourneysActive, baseState, rrNavigate, sections, setActive]
  );

  return navigate;
};
